import { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Button, Alert, Spinner } from 'reactstrap';
import { collection, getDocs, query, updateDoc, doc } from 'firebase/firestore';
import { db, addPrefix } from '../firebase';
import Select from 'react-select';
import { toTitleCase } from '../functions';

function EditDepartmentModal({ open, onClose, ticket, onSave }) {
    const [departments, setDepartments] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [selectedReason, setSelectedReason] = useState(null);
    const [reasonsForDepartment, setReasonsForDepartment] = useState([]);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);

    useEffect(() => {
        if (!open) return;
        setFetching(true);
        const fetchAndSync = async () => {
            try {
                const snapshot = await getDocs(query(collection(db, addPrefix("departments"))));
                const depts = snapshot.docs.map(doc => ({ ...doc.data(), firebaseId: doc.id }));
                setDepartments(depts);

                let defaultDept = ticket?.department ? depts.find(d => d.name === ticket.department) : null;
                let defaultDeptOption = defaultDept ? { value: defaultDept.name, label: defaultDept.name } : null;

                setSelectedDepartment(defaultDeptOption);
                const reasons = defaultDept?.reasons || [];
                setReasonsForDepartment(reasons);

                const ticketReason = ticket?.reasons?.[0]?.trim().toLowerCase();
                const matchedReason = reasons.find(r => r.trim().toLowerCase() === ticketReason);
                const defaultReason = matchedReason || reasons[0] || null;

                setSelectedReason(defaultReason ? { value: defaultReason, label: defaultReason } : null);
            } catch (err) {
                setError('Failed to fetch departments.');
            } finally {
                setFetching(false);
            }
        };
        fetchAndSync();
    }, [open, ticket]);

    useEffect(() => {
        if (!selectedDepartment || departments.length === 0) {
            setReasonsForDepartment([]);
            setSelectedReason(null);
            return;
        }

        const dept = departments.find(d => d.name === selectedDepartment.value);
        const reasons = dept?.reasons || [];
        setReasonsForDepartment(reasons);

        const ticketReason = ticket?.reasons?.[0];
        const isValid = reasons.includes(ticketReason);
        const selected = isValid ? ticketReason : reasons[0] || null;

        setSelectedReason(selected ? { value: selected, label: selected } : null);
    }, [selectedDepartment]);

    const handleSave = async () => {
        setError("");
        setLoading(true);
        try {
            const updatePayload = {
                department: selectedDepartment ? selectedDepartment.value : "",
                reasons: selectedReason ? [selectedReason.value] : [],
                status: "waiting"
            };
            if (selectedDepartment) {
                if (selectedDepartment.value === "Accident") {
                    updatePayload.queue = "FNOL";
                } else {
                    updatePayload.queue = ticket.customBranchId || "";
                }
            }
            await updateDoc(doc(db, addPrefix('tickets'), String(ticket.firebaseId)), updatePayload);
            if (onSave) onSave(selectedDepartment?.value || "", selectedReason?.value || "");
            onClose();
        } catch (err) {
            setError('Failed to update department and reason.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={open} toggle={loading ? undefined : onClose} backdrop={loading ? 'static' : true}>
            <ModalHeader>Change Department(Reason)</ModalHeader>
            <ModalBody>
                {fetching && <div className="text-center my-3"><Spinner size="sm" /> Loading departments...</div>}
                {error && <Alert color="danger">{error}</Alert>}
                <FormGroup>
                    <Label for="departmentSelect">Department</Label>
                    <Select
                        id="departmentSelect"
                        value={selectedDepartment}
                        onChange={setSelectedDepartment}
                        options={departments.map(dep => ({ value: dep.name, label: dep.name }))}
                        placeholder="Select Department"
                        isDisabled={fetching || loading}
                    />
                </FormGroup>
                <br />
                <FormGroup>
                    <Label for="reasonSelect">Reason</Label>
                    <Select
                        id="reasonSelect"
                        value={selectedReason}
                        onChange={setSelectedReason}
                        options={reasonsForDepartment.map(reason => ({ value: reason, label: toTitleCase(reason) }))}
                        placeholder="Select Reason"
                        isDisabled={!selectedDepartment || fetching || loading}
                    />
                </FormGroup>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={onClose} disabled={loading || fetching}>Cancel</Button>
                <Button color="primary" onClick={handleSave} disabled={!selectedDepartment || !selectedReason || loading || fetching}>
                    {loading ? <Spinner size="sm" /> : 'Save'}
                </Button>
            </ModalFooter>
        </Modal>
    );
}

export default EditDepartmentModal;
